import React, {ReactNode, useEffect, useRef, useState} from 'react';
import {NavLink, NavLinkProps} from 'react-router-dom';
import {cx} from '~/lib/cx';
import {classByIntent, Intent} from '~/lib/intent';
import style from './index.module.scss';

interface INavTabs {
    tabs: NavLinkProps[];
}

export const NavTabs = ({tabs}: INavTabs) => <div className={style.navTabs}>
    {tabs.map((navTab, index) => <NavLink
        key={index}
        {...navTab}
        className={({ isActive }) =>
            [style.navTab, navTab.className, isActive && style.navTabActive].filter(Boolean).join(' ')
        }
        onClick={navTab.onClick}
    />)}
</div>;

type ITab<T = any> = {
    title: ReactNode;
    intent?: Intent;
    id?: T;
};

interface ITabsListProps<T> {
    currentTab: T;
    onTabChange: (tab: T) => void;
    tabs: ITab[];
}

export function TabsList<T>({tabs, currentTab, onTabChange}: ITabsListProps<T>) {
    return <ul className={style.tabsList}>
        {tabs.map((tab) => <li
            key={tab.id} className={[style.tab, classByIntent(tab.intent), tab.id === currentTab && style.tabActive].join(' ')}
            onClick={() => onTabChange(tab.id)}>{tab.title}</li>)}
    </ul>;
}

interface ITabsProps<T> {
    tabs: (ITab<T> & { children: ReactNode })  [];
    initialIndex?: number;
    onTabChange?: (index: number) => void;
}

export function TabsWithBody<T>({tabs, initialIndex = 0, onTabChange}: ITabsProps<T>) {
    const [tabIndex, setTabIndex] = useState(initialIndex);
    React.useEffect(() => {
        setTabIndex(initialIndex);
    }, [initialIndex]);
    return <div className={style.tabs}>
        <TabsList currentTab={tabIndex} onTabChange={(tab) => {
            setTabIndex(tab);
            onTabChange?.(tab);
        }} tabs={tabs.map((tab, index) => ({...tab, id: index}))}/>
        {tabs.map((tab, index) => <div key={index} style={{display: tabIndex === index ? 'block' : 'none'}}>{tab.children}</div>)}
    </div>;
}

interface IAccordionProps<T> extends ITabsProps<T> {
    initialIndex?: number | null;
    expandAll?: boolean;
}

export function AccordionWithBody<T>({tabs, initialIndex, expandAll}: IAccordionProps<T>) {
    const [openIndex, setOpenIndex] = useState<number | null>(initialIndex ?? 0);
    const itemRefs = useRef<(HTMLDivElement | null)[]>([]);
    const prevExpandAllRef = useRef<boolean | undefined>(expandAll);

    useEffect(() => {
        if (expandAll || openIndex === null) {
            return;
        }
        const node = itemRefs.current[openIndex];
        if (node && typeof node.scrollIntoView === "function") {
            node.scrollIntoView({ block: "start", behavior: "smooth" });
        }
    }, [openIndex, expandAll]);

    useEffect(() => {
        const prevExpandAll = prevExpandAllRef.current;
        if (prevExpandAll && !expandAll) {
            setOpenIndex(null);
        }
        prevExpandAllRef.current = expandAll;
    }, [expandAll]);

    return (
        <div className={style.accordion}>
            {tabs.map((tab, index) => {
                const isOpen = !!expandAll || openIndex === index;
                const itemClasses = cx(style.accordionItem, classByIntent(tab.intent), isOpen && style.accordionItemOpen);
                const headerClasses = cx(style.accordionHeader, isOpen && style.accordionHeaderOpen);
                const panelClasses = cx(style.accordionPanel, isOpen && style.accordionPanelOpen);

                return (
                    <div
                        key={index}
                        className={itemClasses}
                        ref={(el) => {
                            itemRefs.current[index] = el;
                        }}
                    >
                        <button
                            type="button"
                            className={headerClasses}
                            onClick={() => {
                                if (expandAll) {
                                    return;
                                }
                                setOpenIndex(isOpen ? null : index);
                            }}
                        >
                            <span className={style.accordionTitle}>{tab.title}</span>
                            <i
                                className={cx('far', isOpen ? 'fa-chevron-up' : 'fa-chevron-down', style.accordionIcon)}
                                aria-hidden="true"
                            />
                        </button>
                        <div className={panelClasses}>{tab.children}</div>
                    </div>
                );
            })}
        </div>
    );
}
